import React from 'react';
import { Content } from '../components';
import TableSkeleton from '../Skeletons/TableSkeleton';
import AddEditModal, { AddEditModalState, IaddEditModalStates } from '../pageComponents/Planpatient/AddEditModal';
import { CustomTableRow, Table, TableBody, TableButtons, TableHead, TableNoContent, TableRowTitle } from '../components/Table';
import Pagination from '../components/Pagination';
import DeleteModal, { DeleteModalState, IdeleteModalStates } from '../pageComponents/Planpatient/DeleteModal';
import { planpatientTableHeadData } from '../hooks/planpatient/staticData';
import { useAddRTMTimeLog, useDeleteRtmTimeLog, useGetAllRtmTimeLog } from "../hooks/rtm_time_log/index";
import { IAddRTMTimeLogNewReq } from '../hooks/rtm_time_log/index.type';


const PlanPatientList = () => {
  const [pageNumber, setPageNumber] = React.useState<number>(1);
  const perpageData = 10;
  const [searchTerm, setSearchTerm] = React.useState<string>("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = React.useState<string>("");

  // ====================================== Add edit states start ======================================
  const [addEditModal, setAddEditModal] = React.useState<AddEditModalState>({
    status: false,
  });
  const startaddEditModalStatesValue: IaddEditModalStates = {
    description: { value: "", error: '' },
    duration: { value: "", error: '' },
    customDuration: { value: "", error: '' }
  };
  const [addEditModalStates, setAddEditModalStates] = React.useState<IaddEditModalStates>(startaddEditModalStatesValue);
  // ====================================== Add edit states end ======================================

  // ====================================== Delete states start ======================================
  const [deleteModal, setDeleteModal] = React.useState<DeleteModalState>({
    status: false,
  });

  const startDeleteModalStatesValue = {
    name: { value: '', error: '' },
  };
  const [deleteModalStates, setDeleteModalStates] = React.useState<IdeleteModalStates>(startDeleteModalStatesValue);
  // ====================================== Delete states end ======================================

  // ====================================== Hooks ======================================
  const { RtmTimeLogData, getRtmTimelogData, RtmTimeLogDataLoading } = useGetAllRtmTimeLog({
    pageNumber,
    perpageData,
    searchTerm: debouncedSearchTerm
  });
  const { addRTMTimeLogLoading, addRTMTimeLog } = useAddRTMTimeLog({ RtmTimeLogId: addEditModal.data?._id });
  const { deleteRTMTimeLogLoading, deleteRTMTimeLog } = useDeleteRtmTimeLog({ RTMTimeLogId: deleteModal.data?._id });

  // ====================================== Search functionality ======================================
  // Debounce search term to avoid too many API calls
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      // Reset to first page when searching
      setPageNumber(1);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };
  // ====================================== Search functionality end ======================================

  const onPageChange = (currentPage: { selected: number }) => {
    setPageNumber(currentPage.selected + 1);
  };

  // ====================================== Add edit functions start ======================================
  React.useEffect(() => {
    if (addEditModal.data?._id) {
      setAddEditModalStates({
        duration: { value: addEditModal.data.duration.toString() || "", error: '' },
        customDuration: { value: "", error: '' },
        description: { value: addEditModal.data.description || "", error: '' },
      });
    }
  }, [addEditModal]);

  const handleCloseModal = () => {
    setAddEditModal({ status: false });
    setAddEditModalStates(startaddEditModalStatesValue);
  };

  const handleAddRTMTimeLogAPI = async (formData: IAddRTMTimeLogNewReq) => {
    const data = await addRTMTimeLog(formData);
    if (data?.status === 'success') {
      handleCloseModal();
      getRtmTimelogData();
    }
  };

  const handleAddRTMTimeLog = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    // Form validation
    if (!addEditModalStates.description.value) {
      setAddEditModalStates(prevVal => ({
        ...prevVal,
        description: { ...prevVal.description, error: 'Please enter RTM Time Log description' }
      }));
      return;
    }

    if (!addEditModalStates.duration.value) {
      setAddEditModalStates(prevVal => ({
        ...prevVal,
        duration: { ...prevVal.duration, error: 'Please select duration' }
      }));
      return;
    }

    if (addEditModalStates.duration.value === 'Custom' && !addEditModalStates.customDuration.value) {
      setAddEditModalStates(prevVal => ({
        ...prevVal,
        customDuration: { ...prevVal.customDuration, error: 'Please enter custom duration' }
      }));
      return;
    }

    // Ensure custom duration is a valid number
    if (addEditModalStates.duration.value === 'Custom' && isNaN(Number(addEditModalStates.customDuration.value))) {
      setAddEditModalStates(prevVal => ({
        ...prevVal,
        customDuration: { ...prevVal.customDuration, error: 'Please enter a valid number' }
      }));
      return;
    }

    // Prepare form data for API submission
    const formData: IAddRTMTimeLogNewReq = {
      description: addEditModalStates.description.value,
      duration: addEditModalStates.duration.value,
      customDuration: addEditModalStates.customDuration.value,
    };

    // Submit to API
    handleAddRTMTimeLogAPI(formData);
  };
  // ====================================== Add edit functions end ======================================

  // ====================================== Delete functions start ======================================
  const handleCloseDeleteModal = () => {
    setDeleteModal({ status: false });
    setDeleteModalStates(startDeleteModalStatesValue);
  };

  const handleDeleteRtmTimelog = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (deleteModal.data) {
      const data = await deleteRTMTimeLog();
      if (data?.status === 'success') {
        handleCloseDeleteModal();
        getRtmTimelogData();
      }
    }else{
      console.error("No RTM Time Log selected for deletion");
    }

  };
  // ====================================== Delete functions end ======================================

  return (
    <Content
      currentMenu='plan-patient-list'
      headerTitle='Assign Plan'
      addBtn={true}
      addBtnClick={() => setAddEditModal({ status: true })}
      addBtnTitle='Assign Plan'
      searchBox={true}
      searchPlaceholder='Search assign plan'
      handleSearchChange={handleSearchChange}
      totalReult={RtmTimeLogData && RtmTimeLogData.pagination?.totalRecords > 0 ? RtmTimeLogData.pagination.totalRecords + ' Assign plan' : ''}
    >
      <Table>
        <TableHead data={planpatientTableHeadData} />
        <TableBody>
          <CustomTableRow>
            {RtmTimeLogDataLoading ? (
              <TableSkeleton rows={planpatientTableHeadData.length} columns={5} />
            ) : RtmTimeLogData && RtmTimeLogData.pagination?.totalRecords > 0 ? (
              RtmTimeLogData.data.map((log) => (
                <tr key={log._id}>
                  <td></td>
                  <TableRowTitle>
                    {log.description}
                  </TableRowTitle>
                  <td>{log.duration} Minutes</td>
                  <td>{log.duration} Minutes</td>
                  <td>{log.duration} Minutes</td>
                  <td>{log.duration} Minutes</td>
                  <td>{log.duration} Minutes</td>
                  <td>
                    <button>Unassign</button>
                  </td>
                  <TableButtons
                    buttons={[
                      { icon: 'edit-3', tooltip: 'Edit RTM Time Log', onClick: () => setAddEditModal({ status: true, data: log }) },
                      { icon: 'trash', tooltip: 'Delete RTM Time Log', variant: 'danger', onClick: () => setDeleteModal({ status: true, data: log }) },
                    ]}
                  />
                  <td></td>
                </tr>
              ))
            ) : (
              <TableNoContent colSpan={planpatientTableHeadData.length} message={searchTerm ? 'No RTM Time Log found matching your search' : 'No RTM Time Log found'} />
            )}
          </CustomTableRow>
        </TableBody>
      </Table>

      <Pagination
        itemsPerPage={perpageData}
        totalItems={RtmTimeLogData ? RtmTimeLogData.pagination?.totalRecords : 0}
        onPageChange={onPageChange}
      />

      {addEditModal.status && (
        <AddEditModal
          loading={addRTMTimeLogLoading}
          addEditModal={addEditModal}
          onCloseModal={handleCloseModal}
          addEditModalStates={addEditModalStates}
          setAddEditModalStates={setAddEditModalStates}
          handleAddEdit={handleAddRTMTimeLog}
        />
      )}

      {deleteModal.status && (
        <DeleteModal
          loading={deleteRTMTimeLogLoading}
          deleteModal={deleteModal}
          deleteModalStates={deleteModalStates}
          setDeleteModalStates={setDeleteModalStates}
          onCloseModal={handleCloseDeleteModal}
          handleDelete={handleDeleteRtmTimelog}
        />
      )}
    </Content>
  );
};

export default PlanPatientList;